import { 
  Column, 
  EnquiryHandle, 
  IServerSideGetRowsParams, 
  ServerSideCriteria, 
  useServerEnquiry } from '@access-erp/common';
import { useEffect, useRef, useState } from 'react';

import { Guid } from 'guid-typescript';

import { FilterValueTypes } from '@access-erp/ui-components';

import { useColumns } from './hooks/useColumns';
import useGetBillingCycles from './hooks/useGetBillingCycles';
import { GetPaginatedFilteredBillingCyclesVariables }
  from '@/graphql/contracts/_generated_/getPaginatedFilteredBillingCycles.query';
import { BillingCycleStatusEnum, Forms } from '@/graphql/contracts/_generated_/contracts';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import { BillingCycleTypeWithId } from '@/utils/data-helpers/types';
import { mergeAdvancedFilters } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';

const BillingCyclesComponent = () => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const gridRef = useRef<EnquiryHandle<BillingCycleTypeWithId>>(null);
  const { headerColumns, goToBillingCyclesBySearch } = useColumns();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();
  const { getBillingCycles } = useGetBillingCycles();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const enquiry = useServerEnquiry<
  BillingCycleTypeWithId,
  GetPaginatedFilteredBillingCyclesVariables['filter']
  >({
    gridRef,
    headerColumns: headerColumns as Column<BillingCycleTypeWithId>[],
    initialFilter: mergeAdvancedFilters(
      [
        {
          name: 'processingStatus',
          type: FilterValueTypes.EnumFilter,
          label: 'Processing Status',
          value: {
            containsValues: [BillingCycleStatusEnum.ActivePending],
          },
        },
      ],
      savedFilters,
    ),
  });

  useEffect(() => {
    const getInitialData = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getInitialData();
  }, [getUserRole]);

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  const getData = async (
    params: IServerSideGetRowsParams,
    serverSideCriteria: ServerSideCriteria,
  ) => {
    await getSavedFilterIfAny(Forms.BillingCycles, params);

    const billingCyclesData = await getBillingCycles({
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: serverSideCriteria.userSetSorting,
      group: serverSideCriteria.groupByColumn as any,
    });

    setPageIsLoading(false);

    const searchIsCleared = enquiry.serverSide?.current?.search === '';
    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToBillingCyclesBySearch(enquiry.serverSide?.current?.search);
    }

    return {
      items:
        billingCyclesData?.items?.map((el) => ({
          ...el,
          id: el?.billingCycleID &&
            !Guid.parse(el.billingCycleID).isEmpty()
            ? el.billingCycleID
            : Guid.create().toString(),
        })) ?? [],
      pageInfo: {
        hasNextPage: billingCyclesData?.pageInfo.hasNextPage,
        hasPreviousPage: billingCyclesData?.pageInfo.hasPreviousPage,
        endCursor: billingCyclesData?.pageInfo?.endCursor,
        startCursor: billingCyclesData?.pageInfo?.startCursor,
      },
    };
  };

  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef}
        enquiry={enquiry}
        filterForm={Forms.BillingCycles}
        formName={Forms.BillingCycles}
        headerProps={{
          primaryTitle: 'Billing Cycles',
          secondaryTitle: 'List',
          search: searchTextFromQuery,
          searchPlaceholder: 'Start typing Contract Number or Account Code',
          showExportAll: userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN,
          showExportCurrent: false,
        }}
        detailProps={{
          exportFileName: 'Billing Cycles',
          rowGroupPanelShow: 'always',
          masterDetail: true,
          getData: getData as any,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          loading: pageIsLoading,
        }}
      />
    </>
  );
};

export default BillingCyclesComponent;
